import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import api from '../services/authService';
import { Pagination as PaginationType } from '../types';
import { colors } from '../styles/colors';
import Pagination from './Pagination';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type NpsTab = 'report' | 'responses' | 'config';
type CustomerFilter = 'all' | 'subs' | 'nsubs';

interface NpsGroup {
  count: number;
  percentage: number;
  diff?: number;
}

interface NpsReportData {
  filters: {
    from: string;
    to: string;
    customer: CustomerFilter;
    previousFrom: string;
    previousTo: string;
  };
  current: {
    scores: number[];
    scoreCounts: Array<{ score: number; count: number }>;
    nps: number;
    count: number;
    mean: number;
    promoters: NpsGroup;
    passives: NpsGroup;
    detractors: NpsGroup;
  };
  previous: {
    nps: number;
    count: number;
    mean: number;
  };
  diff: {
    nps: number;
    count: number;
    mean: number;
  };
}

interface NpsResponse {
  id: string;
  company_id?: number;
  recruiter_id?: number;
  score?: number | null;
  comments?: string | null;
  created_at: string;
  company_name?: string | null;
  company_fancy_name?: string | null;
  recruiter_email?: string | null;
  subscriber: number;
}

interface NpsConfig {
  company_nps_first_survey_days: number;
  company_nps_resurvey_throttle_days: number;
  company_nps_decline_throttle_days: number;
  company_nps_modal_delay_seconds: number;
}

const CONFIG_LABELS: Array<{ key: keyof NpsConfig; label: string; suffix: string; helper: string }> = [
  {
    key: 'company_nps_first_survey_days',
    label: 'Primeira pesquisa',
    suffix: 'dias',
    helper: 'Tempo ate mostrar a primeira pesquisa para uma empresa elegivel.',
  },
  {
    key: 'company_nps_resurvey_throttle_days',
    label: 'Nova pesquisa apos resposta',
    suffix: 'dias',
    helper: 'Intervalo para voltar a perguntar quando o usuario respondeu.',
  },
  {
    key: 'company_nps_decline_throttle_days',
    label: 'Nova pesquisa apos recusa',
    suffix: 'dias',
    helper: 'Intervalo para voltar a perguntar quando o usuario recusou.',
  },
  {
    key: 'company_nps_modal_delay_seconds',
    label: 'Delay do modal',
    suffix: 'segundos',
    helper: 'Tempo de espera na pagina antes de abrir a pesquisa.',
  },
];

const getDefaultFrom = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().slice(0, 10);
};

const getDefaultTo = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDiff = (value: number) => {
  if (value > 0) return `+${value}`;
  return `${value}`;
};

const getScoreKind = (score?: number | null) => {
  if (score === null || score === undefined) return 'pending';
  if (score >= 9) return 'promoter';
  if (score >= 7) return 'passive';
  return 'detractor';
};

const COMMENT_PREVIEW_LENGTH = 120;

const getCommentPreview = (comment: string) => {
  if (comment.length <= COMMENT_PREVIEW_LENGTH) return comment;
  return `${comment.slice(0, COMMENT_PREVIEW_LENGTH).trim()}...`;
};

const Nps: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') || 'report') as NpsTab;
  const [activeTab, setActiveTab] = useState<NpsTab>(
    ['report', 'responses', 'config'].includes(initialTab) ? initialTab : 'report'
  );

  const [report, setReport] = useState<NpsReportData | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState({
    from: searchParams.get('from') || getDefaultFrom(),
    to: searchParams.get('to') || getDefaultTo(),
    customer: (searchParams.get('customer') || 'all') as CustomerFilter,
  });

  const [responses, setResponses] = useState<NpsResponse[]>([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responseSearch, setResponseSearch] = useState('');
  const [responseSearchTerm, setResponseSearchTerm] = useState('');
  const [responseSearchType, setResponseSearchType] = useState('company_name');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [responsesPagination, setResponsesPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });

  const [config, setConfig] = useState<NpsConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [selectedComment, setSelectedComment] = useState<NpsResponse | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', activeTab);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'report') fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reportFilters]);

  useEffect(() => {
    if (activeTab === 'responses') fetchResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, responsesPagination.page, responseSearchTerm, responseSearchType, scoreFilter]);

  useEffect(() => {
    if (activeTab === 'config' && !config) fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchReport = async () => {
    try {
      setReportLoading(true);
      const response = await api.get('/nps/report', { params: reportFilters });
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching NPS report:', error);
      toast.error('Erro ao carregar relatorio de NPS');
    } finally {
      setReportLoading(false);
    }
  };

  const fetchResponses = async () => {
    try {
      setResponsesLoading(true);
      const response = await api.get('/nps/responses', {
        params: {
          page: responsesPagination.page,
          limit: responsesPagination.limit,
          search: responseSearchTerm,
          searchType: responseSearchType,
          scoreFilter,
        },
      });
      setResponses(response.data.responses);
      setResponsesPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching NPS responses:', error);
      toast.error('Erro ao carregar respostas de NPS');
    } finally {
      setResponsesLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      setConfigLoading(true);
      const response = await api.get('/nps/config');
      setConfig(response.data.config);
    } catch (error) {
      console.error('Error fetching NPS config:', error);
      toast.error('Erro ao carregar configuracoes de NPS');
    } finally {
      setConfigLoading(false);
    }
  };

  const saveConfig = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!config) return;

    try {
      setSavingConfig(true);
      const response = await api.put('/nps/config', config);
      setConfig(response.data.config);
      toast.success('Configuracoes salvas');
    } catch (error) {
      console.error('Error saving NPS config:', error);
      toast.error('Erro ao salvar configuracoes');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleResponseSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setResponsesPagination(prev => ({ ...prev, page: 1 }));
    setResponseSearchTerm(responseSearch);
  };

  const segmentChartData = useMemo(() => {
    if (!report) return null;
    return {
      labels: ['Detratores', 'Passivos', 'Promotores'],
      datasets: [{
        label: '% das respostas',
        data: [
          report.current.detractors.percentage,
          report.current.passives.percentage,
          report.current.promoters.percentage,
        ],
        backgroundColor: [colors.freelance, colors.warning, colors.success],
        borderWidth: 0,
      }],
    };
  }, [report]);

  const scoreChartData = useMemo(() => {
    if (!report) return null;
    return {
      labels: report.current.scoreCounts.map(item => String(item.score)),
      datasets: [{
        label: 'Respostas',
        data: report.current.scoreCounts.map(item => item.count),
        backgroundColor: report.current.scoreCounts.map(item => {
          if (item.score >= 9) return colors.success;
          if (item.score >= 7) return colors.warning;
          return colors.freelance;
        }),
        borderWidth: 0,
      }],
    };
  }, [report]);

  const trendChartData = useMemo(() => {
    if (!report) return null;
    return {
      labels: [
        `${formatDate(report.filters.previousFrom)} - ${formatDate(report.filters.previousTo)}`,
        `${formatDate(report.filters.from)} - ${formatDate(report.filters.to)}`,
      ],
      datasets: [{
        label: 'NPS',
        data: [report.previous.nps, report.current.nps],
        borderColor: colors.primary,
        backgroundColor: colors.primary,
        tension: 0.3,
      }],
    };
  }, [report]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const renderDiff = (value: number) => (
    <span style={{
      ...styles.diffBadge,
      ...(value > 0 ? styles.diffPositive : {}),
      ...(value < 0 ? styles.diffNegative : {}),
    }}>
      {formatDiff(value)}
    </span>
  );

  const renderReport = () => (
    <div>
      <div style={styles.filters}>
        <label style={styles.field}>
          <span style={styles.label}>De</span>
          <input
            type="date"
            value={reportFilters.from}
            onChange={event => setReportFilters(prev => ({ ...prev, from: event.target.value }))}
            style={styles.input}
          />
        </label>
        <label style={styles.field}>
          <span style={styles.label}>Ate</span>
          <input
            type="date"
            value={reportFilters.to}
            onChange={event => setReportFilters(prev => ({ ...prev, to: event.target.value }))}
            style={styles.input}
          />
        </label>
        <div style={styles.segmented}>
          {[
            ['all', 'Todos'],
            ['subs', 'Assinantes'],
            ['nsubs', 'Avulsos'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setReportFilters(prev => ({ ...prev, customer: value as CustomerFilter }))}
              style={{
                ...styles.segmentButton,
                ...(reportFilters.customer === value ? styles.segmentButtonActive : {}),
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {reportLoading && !report ? (
        <div style={styles.loading}>Carregando relatorio...</div>
      ) : report ? (
        <>
          <div style={styles.metricsGrid}>
            <div style={styles.metricCard}>
              <span style={styles.metricLabel}>Pontuacao NPS</span>
              <strong style={styles.metricValue}>{report.current.nps}</strong>
              <span style={styles.previousText}>
                Anterior {report.previous.nps} {renderDiff(report.diff.nps)}
              </span>
            </div>
            <div style={styles.metricCard}>
              <span style={styles.metricLabel}>Respostas</span>
              <strong style={styles.metricValue}>{report.current.count}</strong>
              <span style={styles.previousText}>
                Anterior {report.previous.count} {renderDiff(report.diff.count)}
              </span>
            </div>
            <div style={styles.metricCard}>
              <span style={styles.metricLabel}>Media das notas</span>
              <strong style={styles.metricValue}>{report.current.mean}</strong>
              <span style={styles.previousText}>
                Anterior {report.previous.mean} {renderDiff(report.diff.mean)}
              </span>
            </div>
          </div>

          <div style={styles.reportGrid}>
            <section style={styles.panel}>
              <h3 style={styles.panelTitle}>Distribuicao NPS</h3>
              <div style={styles.chartHeight}>
                {segmentChartData && <Bar data={segmentChartData} options={chartOptions} />}
              </div>
              <div style={styles.groupList}>
                {[
                  ['Detratores', report.current.detractors, colors.freelance],
                  ['Passivos', report.current.passives, colors.warning],
                  ['Promotores', report.current.promoters, colors.success],
                ].map(([label, group, color]) => (
                  <div style={styles.groupItem} key={label as string}>
                    <span style={{ ...styles.groupDot, backgroundColor: color as string }} />
                    <span style={styles.groupName}>{label as string}</span>
                    <strong>{(group as NpsGroup).count}</strong>
                    <span style={styles.groupPercent}>{(group as NpsGroup).percentage}%</span>
                    {renderDiff((group as NpsGroup).diff || 0)}
                  </div>
                ))}
              </div>
            </section>

            <section style={styles.panel}>
              <h3 style={styles.panelTitle}>Notas 0 a 10</h3>
              <div style={styles.chartHeight}>
                {scoreChartData && <Bar data={scoreChartData} options={chartOptions} />}
              </div>
            </section>

            <section style={styles.panelWide}>
              <h3 style={styles.panelTitle}>Comparativo com periodo anterior</h3>
              <div style={styles.chartHeightSmall}>
                {trendChartData && <Line data={trendChartData} options={chartOptions} />}
              </div>
            </section>
          </div>
        </>
      ) : (
        <div style={styles.emptyState}>Nenhum dado encontrado</div>
      )}
    </div>
  );

  const renderResponses = () => (
    <div>
      <div style={styles.responseFilters}>
        {[
          ['all', 'Todos'],
          ['answered', 'Com score'],
          ['pending', 'Sem score'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setScoreFilter(value);
              setResponsesPagination(prev => ({ ...prev, page: 1 }));
            }}
            style={{
              ...styles.responseFilterButton,
              ...(scoreFilter === value ? styles.responseFilterButtonActive : {}),
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={handleResponseSearch} style={styles.listFilters}>
        <select
          value={responseSearchType}
          onChange={event => {
            setResponseSearchType(event.target.value);
            setResponsesPagination(prev => ({ ...prev, page: 1 }));
          }}
          style={styles.select}
        >
          <option value="company_name">Nome da empresa</option>
          <option value="company_id">ID da empresa</option>
          <option value="recruiter_email">Email recrutador</option>
        </select>
        <input
          type="text"
          value={responseSearch}
          onChange={event => setResponseSearch(event.target.value)}
          placeholder={
            responseSearchType === 'company_id'
              ? 'Buscar por ID da empresa...'
              : responseSearchType === 'recruiter_email'
                ? 'Buscar por email do recrutador...'
                : 'Buscar por nome da empresa...'
          }
          style={styles.listInput}
        />
        <button type="submit" style={styles.searchBtn}>Buscar</button>
      </form>

      {responsesLoading ? (
        <div style={styles.loading}>Carregando respostas...</div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Data</th>
                  <th style={styles.th}>Empresa</th>
                  <th style={styles.th}>Recrutador</th>
                  <th style={styles.th}>Score</th>
                  <th style={styles.th}>Tipo</th>
                  <th style={styles.th}>Comentario</th>
                </tr>
              </thead>
              <tbody>
                {responses.map(response => {
                  const scoreKind = getScoreKind(response.score);
                  return (
                    <tr key={response.id}>
                      <td style={styles.td}>{formatDateTime(response.created_at)}</td>
                      <td style={styles.td}>
                        <strong>{response.company_name || response.company_fancy_name || '-'}</strong>
                        {response.company_id && <div style={styles.muted}>#{response.company_id}</div>}
                      </td>
                      <td style={styles.td}>{response.recruiter_email || '-'}</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.scoreBadge,
                          ...(scoreKind === 'promoter' ? styles.scorePromoter : {}),
                          ...(scoreKind === 'passive' ? styles.scorePassive : {}),
                          ...(scoreKind === 'detractor' ? styles.scoreDetractor : {}),
                          ...(scoreKind === 'pending' ? styles.scorePending : {}),
                        }}>
                          {response.score ?? '-'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={response.subscriber ? styles.subscriberBadge : styles.singleBadge}>
                          {response.subscriber ? 'Assinante' : 'Avulso'}
                        </span>
                      </td>
                      <td style={styles.tdComment}>
                        {response.comments ? (
                          <div style={styles.commentPreview}>
                            <span>{getCommentPreview(response.comments)}</span>
                            {response.comments.length > COMMENT_PREVIEW_LENGTH && (
                              <button
                                type="button"
                                onClick={() => setSelectedComment(response)}
                                style={styles.commentButton}
                              >
                                Ver mais
                              </button>
                            )}
                          </div>
                        ) : (
                          <span style={styles.muted}>-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {responses.length === 0 && (
              <div style={styles.emptyState}>Nenhuma resposta encontrada</div>
            )}
          </div>

          <div style={styles.paginationInfo}>
            Pagina {responsesPagination.page} de {responsesPagination.totalPages} ({responsesPagination.total} total)
          </div>
          <Pagination
            currentPage={responsesPagination.page}
            totalPages={responsesPagination.totalPages}
            onPageChange={page => setResponsesPagination(prev => ({ ...prev, page }))}
          />
        </>
      )}
    </div>
  );

  const renderConfig = () => (
    <div style={styles.configShell}>
      {configLoading || !config ? (
        <div style={styles.loading}>Carregando configuracoes...</div>
      ) : (
        <form onSubmit={saveConfig} style={styles.configForm}>
          {CONFIG_LABELS.map(item => (
            <label style={styles.configRow} key={item.key}>
              <span style={styles.configText}>
                <strong>{item.label}</strong>
                <small>{item.helper}</small>
              </span>
              <span style={styles.numberWrap}>
                <input
                  type="number"
                  min={0}
                  value={config[item.key]}
                  onChange={event => setConfig(prev => prev ? ({
                    ...prev,
                    [item.key]: Number.parseInt(event.target.value || '0', 10),
                  }) : prev)}
                  style={styles.numberInput}
                />
                <span style={styles.suffix}>{item.suffix}</span>
              </span>
            </label>
          ))}
          <div style={styles.configActions}>
            <button type="submit" disabled={savingConfig} style={styles.primaryButton}>
              {savingConfig ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>NPS</h1>
          <p style={styles.subtitle}>Configuracao, relatorios e respostas das pesquisas de empresas.</p>
        </div>
      </div>

      <div style={styles.tabs}>
        {[
          ['report', 'Relatorios'],
          ['responses', 'Respostas'],
          ['config', 'Configuração'],
        ].map(([tab, label]) => {
          const selected = activeTab === tab;

          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab as NpsTab)}
              style={{
                ...styles.tabButton,
                ...(selected ? styles.tabButtonActive : {}),
              }}
            >
              <span>{label}</span>
              <span
                style={{
                  ...styles.tabUnderline,
                  ...(selected ? {} : styles.tabUnderlineInactive),
                }}
              />
            </button>
          );
        })}
      </div>

      {activeTab === 'report' && renderReport()}
      {activeTab === 'responses' && renderResponses()}
      {activeTab === 'config' && renderConfig()}

      {selectedComment && (
        <div style={styles.modalOverlay} onClick={() => setSelectedComment(null)}>
          <div style={styles.commentModal} onClick={event => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Comentário NPS</h2>
                <p style={styles.modalSubtitle}>
                  {selectedComment.company_name || selectedComment.company_fancy_name || 'Empresa não informada'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedComment(null)}
                style={styles.modalCloseButton}
              >
                ×
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.commentText}>{selectedComment.comments}</p>
            </div>
            <div style={styles.modalFooter}>
              <button
                type="button"
                onClick={() => setSelectedComment(null)}
                style={styles.primaryButton}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '2rem',
    color: colors.primary,
    margin: 0,
  },
  subtitle: {
    margin: '0.35rem 0 0',
    color: colors.textLight,
  },
  tabs: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '2rem',
  },
  tabButton: {
    display: 'inline-flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.65rem',
    appearance: 'none' as const,
    border: '0 none transparent',
    borderBottom: '0 none transparent',
    outline: 'none',
    boxShadow: 'none',
    textDecoration: 'none',
    backgroundColor: 'transparent',
    color: colors.textLight,
    padding: '1rem 1.2rem 0',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '1.08rem',
    lineHeight: 1.2,
  },
  tabButtonActive: {
    color: colors.primary,
  },
  tabUnderline: {
    width: '100%',
    height: '3px',
    backgroundColor: colors.primary,
    display: 'block',
  },
  tabUnderlineInactive: {
    backgroundColor: 'transparent',
  },
  filters: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap' as const,
    gap: '1rem',
    marginBottom: '1.5rem',
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '1rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem',
  },
  label: {
    fontSize: '0.8rem',
    color: colors.textLight,
    fontWeight: 700,
  },
  input: {
    padding: '0.65rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    minHeight: '40px',
  },
  segmented: {
    display: 'flex',
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    overflow: 'hidden',
  },
  segmentButton: {
    border: 'none',
    borderRight: `1px solid ${colors.border}`,
    backgroundColor: colors.white,
    color: colors.text,
    padding: '0.7rem 0.9rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  segmentButtonActive: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '1.25rem',
    marginBottom: '1.5rem',
  },
  metricCard: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '1.5rem',
  },
  metricLabel: {
    display: 'block',
    color: colors.textLight,
    fontWeight: 700,
    marginBottom: '0.75rem',
  },
  metricValue: {
    display: 'block',
    color: colors.primary,
    fontSize: '2.5rem',
    lineHeight: 1,
  },
  previousText: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginTop: '0.85rem',
    color: colors.textLight,
  },
  diffBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '36px',
    padding: '0.15rem 0.35rem',
    borderRadius: '4px',
    backgroundColor: colors.light,
    color: colors.textLight,
    fontSize: '0.8rem',
    fontWeight: 700,
  },
  diffPositive: {
    color: colors.success,
    backgroundColor: '#e8f5e9',
  },
  diffNegative: {
    color: colors.danger,
    backgroundColor: '#ffebee',
  },
  reportGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(520px, 1fr))',
    gap: '1.5rem',
  },
  panel: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '1.5rem',
  },
  panelWide: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '1.5rem',
    gridColumn: '1 / -1',
  },
  panelTitle: {
    margin: '0 0 1.25rem',
    color: colors.primary,
    fontSize: '1.2rem',
  },
  chartHeight: {
    height: '420px',
  },
  chartHeightSmall: {
    height: '320px',
  },
  groupList: {
    display: 'grid',
    gap: '0.55rem',
    marginTop: '1rem',
  },
  groupItem: {
    display: 'grid',
    gridTemplateColumns: '14px 1fr auto auto auto',
    alignItems: 'center',
    gap: '0.65rem',
    color: colors.text,
  },
  groupDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  groupName: {
    color: colors.textLight,
  },
  groupPercent: {
    color: colors.textLight,
    minWidth: '42px',
    textAlign: 'right' as const,
  },
  responseFilters: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  responseFilterButton: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  responseFilterButtonActive: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  listFilters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  select: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '180px',
    backgroundColor: colors.white,
  },
  listInput: {
    flex: 1,
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '320px',
  },
  searchBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  primaryButton: {
    padding: '0.65rem 1rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  tableContainer: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    minWidth: '980px',
  },
  th: {
    padding: '0.85rem',
    backgroundColor: colors.light,
    color: colors.text,
    textAlign: 'left' as const,
    borderBottom: `1px solid ${colors.border}`,
    fontWeight: 700,
  },
  td: {
    padding: '0.85rem',
    borderBottom: `1px solid ${colors.borderLight}`,
    color: colors.text,
    verticalAlign: 'top' as const,
  },
  tdComment: {
    padding: '0.85rem',
    borderBottom: `1px solid ${colors.borderLight}`,
    color: colors.text,
    verticalAlign: 'top' as const,
    maxWidth: '360px',
  },
  muted: {
    color: colors.textLight,
    fontSize: '0.8rem',
    marginTop: '0.2rem',
  },
  commentPreview: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    gap: '0.5rem',
    lineHeight: 1.45,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  },
  commentButton: {
    padding: '0',
    backgroundColor: colors.white,
    color: colors.primary,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 700,
    textDecoration: 'underline',
    whiteSpace: 'nowrap' as const,
  },
  scoreBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '34px',
    height: '30px',
    borderRadius: '4px',
    color: colors.white,
    fontWeight: 800,
  },
  scorePromoter: {
    backgroundColor: colors.success,
  },
  scorePassive: {
    backgroundColor: colors.warning,
    color: colors.text,
  },
  scoreDetractor: {
    backgroundColor: colors.freelance,
  },
  scorePending: {
    backgroundColor: colors.textMuted,
  },
  subscriberBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    backgroundColor: '#e8f5e9',
    color: colors.success,
    fontWeight: 700,
    fontSize: '0.8rem',
  },
  singleBadge: {
    display: 'inline-block',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    backgroundColor: colors.light,
    color: colors.textLight,
    fontWeight: 700,
    fontSize: '0.8rem',
  },
  paginationInfo: {
    marginTop: '1rem',
    textAlign: 'center' as const,
    color: colors.textLight,
  },
  configShell: {
    width: '100%',
  },
  configForm: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  configRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: '2rem',
    padding: '1.5rem 1.75rem',
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  configText: {
    display: 'grid',
    gap: '0.45rem',
    color: colors.text,
    fontSize: '1.05rem',
  },
  numberWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  numberInput: {
    width: '120px',
    padding: '0.8rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1.1rem',
  },
  suffix: {
    color: colors.textLight,
    minWidth: '70px',
    fontSize: '1rem',
  },
  configActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '1.25rem 1.75rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: colors.textLight,
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: colors.textLight,
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    zIndex: 3000,
  },
  commentModal: {
    width: 'min(720px, 100%)',
    maxHeight: '80vh',
    backgroundColor: colors.white,
    borderRadius: '8px',
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '1.25rem 1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  modalTitle: {
    margin: 0,
    color: colors.primary,
    fontSize: '1.4rem',
  },
  modalSubtitle: {
    margin: '0.35rem 0 0',
    color: colors.textLight,
  },
  modalCloseButton: {
    width: '36px',
    height: '36px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: colors.light,
    color: colors.text,
    cursor: 'pointer',
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  modalBody: {
    padding: '1.5rem',
    overflowY: 'auto' as const,
  },
  commentText: {
    margin: 0,
    color: colors.text,
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '1rem 1.5rem',
    borderTop: `1px solid ${colors.border}`,
  },
};

export default Nps;
